import React, {
  useEffect,
  useMemo,
  useState,
  useLayoutEffect,
  useRef,
} from "react";

import { Grid, GridColumn } from "@progress/kendo-react-grid";

import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";

import { Button } from "@progress/kendo-react-buttons";

import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";

import { DropDownList } from "@progress/kendo-react-dropdowns";

import {
  Landmark,
  Search,
  MoreVertical,
  LayoutGrid,
  List,
  MessageSquare,
  Filter,
  Eye,
  Plus,
  Edit2,
  Target,
  Paperclip,
  EyeOff,
  Clock3,
  RefreshCw,
  CheckCircle2,
  CircleAlert,
  XCircle,
  PauseCircle,
  Ban,
  FileClock,
  ShieldCheck,
  User,
  CalendarDays,
  Building2,
  CreditCard,
  MapPin,
  Mail,
  Phone,
  FileText,
  History,
  ArrowRight,
  UserRound,
  AtSign,
} from "lucide-react";

import { appealApi } from "../../api/appealApi";
import { eftEnrollmentApi } from "../../api/eftEnrollmentApi";

import { isApiConfigured } from "../../api/axiosClient";

import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";

import { CardsPagination } from "../../components/CardsPagination";

import { useToast } from "../../components/Toast";

import { useAuth } from "../../auth/AuthContext";

import { process } from "@progress/kendo-data-query";
import AppealAttachmentsDialog from "./appeal/AppealAttachmentsDialog";
import AttachmentPreviewDialog from "./appeal/AttachmentPreviewDialog";

import { usePermission } from "../../hooks/usePermission";

/* ============================================================

   CONSTANTS

============================================================ */

const STATUS_OPTIONS = [
  "Submitted",
  "Under Review",
  "Pending Information",
  "Approved",
  "Denied",
  "Closed",
  "Resubmitted",
];

const STATUS_META = {
  Submitted: {
    bg: "#DBEAFE",
    color: "#2563EB",
    border: "rgba(37,99,235,0.22)",
    icon: FileClock,
  },

  "Under Review": {
    bg: "#EDE9FE",
    color: "#7C3AED",
    border: "rgba(124,58,237,0.22)",
    icon: Clock3,
  },

  "Pending Information": {
    bg: "#FEF3C7",
    color: "#B45309",
    border: "rgba(180,83,9,0.22)",
    icon: CircleAlert,
  },

  Approved: {
    bg: "#D1FAE5",
    color: "#059669",
    border: "rgba(5,150,105,0.22)",
    icon: CheckCircle2,
  },

  Denied: {
    bg: "#FEE2E2",
    color: "#DC2626",
    border: "rgba(220,38,38,0.22)",
    icon: XCircle,
  },

  Closed: {
    bg: "#F3F4F6",
    color: "#6B7280",
    border: "rgba(107,114,128,0.22)",
    icon: Ban,
  },

  Resubmitted: {
    bg: "#CCFBF1",
    color: "#0F766E",
    border: "rgba(15,118,110,0.22)",
    icon: FileClock,
  },
};

/* ============================================================

   HELPERS

============================================================ */

//const formatDate = (value) => {
//  if (!value) return "—";

//  const date = new Date(value);

//  if (Number.isNaN(date.getTime())) {
//    return value;
//  }

//  return date.toLocaleDateString("en-US", {
//    month: "short",

//    day: "numeric",

//    year: "numeric",
//  });
//};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-US", {
    //   timeZone: 'UTC',
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

//const formatDateTime = (value) => {
//  if (!value) return "—";

//  const date = new Date(value);

//  if (Number.isNaN(date.getTime())) {
//    return value;
//  }

//  return date.toLocaleString("en-US", {
//    month: "short",

//    day: "numeric",

//    year: "numeric",

//    hour: "numeric",

//    minute: "2-digit",
//  });
//};

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-US", {
    //timeZone: 'UTC',
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const relativeDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const diff = Date.now() - date.getTime();

  if (diff < 0) return "upcoming";

  const minutes = Math.floor(diff / 60000);

  const hours = Math.floor(diff / 3600000);

  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "just now";

  if (minutes < 60) return `${minutes}m ago`;

  if (hours < 24) return `${hours}h ago`;

  if (days < 2) return "yesterday";

  if (days < 7) return `${days}d ago`;

  if (days < 30) return `${Math.floor(days / 7)}w ago`;

  return formatDate(value);
};

const getClaimAppealName = (item) => {
  return item.providerName || "Unknown Provider";
};

const getAppealId = (item) => {
  return item.id || item.AppealId;
};

const getConfirmationNumber = (item) => {
  return item.confirmationNumber || "";
};

const StatusPill = ({ status }) => {
  const config = STATUS_META[status] || {
    bg: "rgba(156,163,175,0.12)",

    color: "#6B7280",

    border: "rgba(156,163,175,0.25)",
  };

  return (
    <span
      style={{
        display: "inline-flex",

        alignItems: "center",

        gap: 6,

        padding: "4px 10px 4px 8px",

        borderRadius: 999,

        fontSize: 12,

        fontWeight: 700,

        background: config.bg,

        color: config.color,

        border: `1px solid ${config.border}`,

        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 7,

          height: 7,

          borderRadius: "50%",

          background: config.color,
        }}
      />

      {status || "Unknown"}
    </span>
  );
};

/* ============================================================

   STATUS BADGE

============================================================ */

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || {
    bg: "#F3F4F6",

    color: "#6B7280",

    border: "rgba(107,114,128,0.2)",

    icon: CircleAlert,
  };

  const Icon = meta.icon;

  return (
    <span
      style={{
        display: "inline-flex",

        alignItems: "center",

        gap: 5,

        padding: "5px 10px",

        borderRadius: 999,

        fontSize: 11,

        fontWeight: 700,

        background: meta.bg,

        color: meta.color,

        border: `1px solid ${meta.border}`,

        whiteSpace: "nowrap",
      }}
    >
      <Icon size={12} strokeWidth={2.4} />

      {status || "Unknown"}
    </span>
  );
};

/* ============================================================
   ENROLLMENT CARD

============================================================ */

const AppealCard = ({
  appeal,
  user,
  onChangeStatus,
  onHistory,
  onView,
  onAttachments,
  onEdit,
  onSavedState,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const status = appeal.status;

  const navigate = useNavigate();

  //const handleAppealAction = (appealId,claimNumber) => {
  //    navigate(`/claims/${claimNumber}/appeal?aid=${appealId}`);
  //};

  return (
      <div className="card-scroll-container">
          <div
              className="responsive-card-grid"
              style={{
                  background: "#fff",
                  border: "1px solid #E8ECF0",
                  borderRadius: 16,
                  padding: 18,
                  position: "relative",
                  transition: "all .2s",
                  boxShadow: "0 1px 2px rgba(0,0,0,.03)",
              }}
              data-cy="appealCard"
              onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#CBD5E1";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.06)";
                  e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#E8ECF0";
                  e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,.03)";
                  e.currentTarget.style.transform = "translateY(0)";
              }}
          >
              {/* Header */}
              <div
                  style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      marginBottom: 14,
                  }}
              >
                  <div
                      style={{
                          width: 46,
                          height: 46,
                          borderRadius: 13,
                          background: "rgba(13,148,136,.09)",
                          color: "#0D9488",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          border: "1px solid rgba(13,148,136,.15)",
                      }}
                  >
                      <User size={22} strokeWidth={2} />
                  </div>

                  <div
                      style={{
                          flex: 1,
                          minWidth: 0,
                      }}
                  >
                      {/* Provider / Member Name */}
                      <div
                          style={{
                              fontSize: 15,
                              fontWeight: 700,
                              color: "#1A1D21",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                          }}
                      >
                          {appeal.memberName || appeal.providerName || "Appeal"}
                      </div>

                      {/* Appeal / Claim Number */}
                      <div
                          style={{
                              marginTop: 3,
                              fontSize: 11,
                              color: "#9CA3AF",
                              fontFamily: "monospace",
                              display: "flex",
                              gap: "20px",
                              flexWrap: "wrap",
                          }}
                      >
                          <span>Appeal #{appeal.appealNumber || appeal.appealId}</span>

                          {appeal.claimNumber && (
                              <span>
                                  {/*Claim #{appeal.claimNumber}*/}
                                  Claim #
                                  <Link
                                      name="linkClaimDetailsCard"
                                      /*state={{*/
                                      /*    fromClaimAppealList*/
                                      /*}}*/

                                      onClick={(e) => {
                                          e.preventDefault();
                                          onSavedState();
                                          navigate(
                                              `/claims/${appeal.claimNumber}/null/${appeal.tin}`,
                                              {
                                                  state: {
                                                      fromClaimAppealList: true,
                                                  },
                                              },
                                          );
                                      }}

                                      /*to={`/claims/${appeal.claimNumber}/null/${appeal.tin}`}*/
                                      to="#"
                                      data-cy="cardLinkNaviToClaim"
                                      style={{
                                          fontWeight: 700,
                                          color: "#0D9488",
                                          textDecoration: "none",
                                          fontFamily: "monospace",
                                      }}
                                      onMouseOver={(e) =>
                                          (e.target.style.textDecoration = "underline")
                                      }
                                      onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                                  >
                                      {appeal.claimNumber}
                                  </Link>
                              </span>
                          )}
                      </div>

                      {/* Status + Reason */}
                      <div
                          style={{
                              marginTop: 7,
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              flexWrap: "wrap",
                          }}
                      >
                          <StatusBadge status={status} />

                          {appeal.reason && (
                              <span
                                  style={{
                                      padding: "4px 8px",
                                      borderRadius: 999,
                                      background: "#F8FAFC",
                                      border: "1px solid #E8ECF0",
                                      color: "#64748B",
                                      fontSize: 10.5,
                                      fontWeight: 600,
                                  }}
                              >
                                  {appeal.reason}
                              </span>
                          )}
                      </div>
                  </div>

                  {/* Menu */}
                  <div style={{ position: "relative" }}>
                      <button
                          onClick={() => setMenuOpen(!menuOpen)}
                          data-cy="cardMenuButton"
                          style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              border: "none",
                              background: menuOpen ? "#F3F4F6" : "transparent",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#6B7280",
                          }}
                          aria-label="More options"
                      >
                          <MoreVertical size={16} />
                      </button>

                      {menuOpen && (
                          <>
                              <div
                                  style={{
                                      position: "fixed",
                                      inset: 0,
                                      zIndex: 10,
                                  }}
                                  onClick={() => setMenuOpen(false)}
                              />

                              <div
                                  style={{
                                      position: "absolute",
                                      right: 0,
                                      top: 36,
                                      zIndex: 11,
                                      background: "#fff",
                                      border: "1px solid #E8ECF0",
                                      borderRadius: 12,
                                      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
                                      minWidth: 190,
                                      padding: 6,
                                      overflow: "hidden",
                                  }}
                              >
                                  <MenuItem
                                      icon={Eye}
                                      label="View appeal"
                                      data-cy="cardMenuViewAppeal"
                                      onClick={() => {
                                          setMenuOpen(false);
                                          onView(appeal);
                                      }}
                                  />

                                  <MenuItem
                                      icon={Edit2}
                                      label="Edit appeal"
                                      data-cy="cardMenuEditAppeal"
                                      color="#0D9488"
                                      onClick={() => {
                                          setMenuOpen(false);

                                          //handleAppealAction(
                                          //    appeal.appealId,appeal.claimNumber
                                          //);

                                          onEdit(appeal.appealId, appeal.claimNumber, appeal.userId);
                                      }}
                                  />
                                  {["super admin", "admin"].includes(
                                      user?.userRole?.trim().toLowerCase(),
                                  ) && (
                                          <>
                                              <MenuItem
                                                  icon={History}
                                                  label="Status history"
                                                  data-cy="cardMenuStatusHistory"
                                                  color="#7C3AED"
                                                  onClick={() => {
                                                      setMenuOpen(false);
                                                      onHistory(appeal);
                                                  }}
                                              />
                                          </>
                                      )}
                              </div>
                          </>
                      )}
                  </div>
              </div>

              {/* Appeal Information */}
              <div
                  style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 9,
                      marginBottom: 14,
                  }}
              >
                  <InfoRow icon={User} label="Member" value={appeal.memberName} />

                  <InfoRow
                      icon={CreditCard}
                      label="Member ID"
                      value={appeal.memberId}
                      mono
                  />

                  <InfoRow
                      icon={Building2}
                      label="Provider"
                      value={appeal.providerName}
                  />

                  {/*<InfoRow*/}
                  {/*    icon={CreditCard}*/}
                  {/*    label="NPI"*/}
                  {/*    value={appeal.npi}*/}
                  {/*    mono*/}
                  {/*/>*/}

                  <InfoRow icon={FileText} label="TIN" value={appeal.tin} mono />

                  {/*<InfoRow*/}
                  {/*    icon={FileText}*/}
                  {/*    label="Claim"*/}
                  {/*    value={appeal.claimNumber}*/}
                  {/*    mono*/}
                  {/*/>*/}

                  <InfoRow
                      icon={Mail}
                      label="Contact Email"
                      value={appeal.contactEmail}
                  />

                  <InfoRow
                      icon={Phone}
                      label="Contact Phone"
                      value={appeal.contactPhone}
                  />

                  {/*<InfoRow*/}
                  {/*    icon={Target}*/}
                  {/*    label="Requested Outcome"*/}
                  {/*    value={appeal.requestedOutcome}*/}
                  {/*/>*/}

                  {/*<InfoRow*/}
                  {/*    icon={Paperclip}*/}
                  {/*    label="Attachments"*/}
                  {/*    value={appeal.attachmentCount ?? 0}*/}
                  {/*/>*/}

                  <InfoRow
                      icon={CalendarDays}
                      label="Submitted"
                      value={formatDate(appeal.submissionDate)}
                  />
              </div>

              {/* Admin Note */}
              {appeal.adminNote && (
                  <div
                      style={{
                          padding: "9px 11px",
                          borderRadius: 9,
                          background: "#F8FAFC",
                          border: "1px solid #EEF2F7",
                          marginBottom: 14,
                      }}
                  >
                      <div
                          style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 5,
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: "#64748B",
                              marginBottom: 4,
                          }}
                      >
                          <FileText size={12} />
                          Admin Note
                      </div>

                      <div
                          style={{
                              fontSize: 12,
                              color: "#475569",
                              lineHeight: 1.45,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                          }}
                      >
                          {appeal.adminNote}
                      </div>
                  </div>
              )}

              {/* User Registered Information */}
              {/*{(appeal.registeredUserName ||*/}
              {/*  appeal.registeredFirstName ||*/}
              {/*  appeal.registeredLastName ||*/}
              {/*  appeal.registeredEmail) &&*/}
              {/*  user?.userRole?.trim().toLowerCase() === "super admin" && (*/}
              {/*    <div*/}
              {/*      style={{*/}
              {/*        padding: "10px 12px",*/}
              {/*        borderRadius: 10,*/}
              {/*        background: "#F8FAFC",*/}
              {/*        border: "1px solid #E2E8F0",*/}
              {/*        marginBottom: 14,*/}
              {/*      }}*/}
              {/*    >*/}
              {/*      <div*/}
              {/*        style={{*/}
              {/*          display: "flex",*/}
              {/*          alignItems: "center",*/}
              {/*          gap: 5,*/}
              {/*          fontSize: 10.5,*/}
              {/*          fontWeight: 700,*/}
              {/*          color: "#64748B",*/}
              {/*          marginBottom: 7,*/}
              {/*        }}*/}
              {/*      >*/}
              {/*        <User size={12} />*/}
              {/*        Provider Admin User*/}
              {/*      </div>*/}

              {/*      {appeal.registeredUserName && (*/}
              {/*        <div*/}
              {/*          style={{*/}
              {/*            fontSize: 12,*/}
              {/*            fontWeight: 600,*/}
              {/*            color: "#334155",*/}
              {/*            marginBottom: 2,*/}
              {/*            overflow: "hidden",*/}
              {/*            textOverflow: "ellipsis",*/}
              {/*            whiteSpace: "nowrap",*/}
              {/*          }}*/}
              {/*        >*/}
              {/*          @{appeal.registeredUserName}*/}
              {/*        </div>*/}
              {/*      )}*/}

              {/*      {(appeal.registeredFirstName || appeal.registeredLastName) && (*/}
              {/*        <div*/}
              {/*          style={{*/}
              {/*            fontSize: 11.5,*/}
              {/*            color: "#64748B",*/}
              {/*            marginBottom: appeal.registeredEmail ? 4 : 0,*/}
              {/*            overflow: "hidden",*/}
              {/*            textOverflow: "ellipsis",*/}
              {/*            whiteSpace: "nowrap",*/}
              {/*          }}*/}
              {/*        >*/}
              {/*          {[appeal.registeredFirstName, appeal.registeredLastName]*/}
              {/*            .filter(Boolean)*/}
              {/*            .join(" ")}*/}
              {/*        </div>*/}
              {/*      )}*/}

              {/*      {appeal.registeredEmail && (*/}
              {/*        <div*/}
              {/*          title={appeal.registeredEmail}*/}
              {/*          style={{*/}
              {/*            fontSize: 10.5,*/}
              {/*            color: "#94A3B8",*/}
              {/*            overflow: "hidden",*/}
              {/*            textOverflow: "ellipsis",*/}
              {/*            whiteSpace: "nowrap",*/}
              {/*          }}*/}
              {/*        >*/}
              {/*          {appeal.registeredEmail}*/}
              {/*        </div>*/}
              {/*      )}*/}
              {/*    </div>*/}
              {/*  )}*/}

              {/* Footer */}
              <div
                  style={{
                      borderTop: "1px solid #F1F5F9",
                      paddingTop: 12,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      overflowX: "auto",
                  }}
              >
                  <div
                      style={{
                          fontSize: 11,
                          color: "#9CA3AF",
                      }}
                  >
                      Submitted {relativeDate(appeal.submissionDate)}
                  </div>

                  <div
                      style={{
                          display: "flex",
                          gap: 6,
                      }}
                  >
                      <button
                          onClick={() => onView(appeal)}
                          data-cy="cardViewButton"
                          style={{
                              padding: "6px 12px",
                              borderRadius: 8,
                              border: "1px solid rgba(13,148,136,0.2)",
                              background: "rgba(13,148,136,0.08)",
                              color: "#0D9488",
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              fontFamily: "inherit",
                          }}
                      >
                          <Eye size={12} />
                          View
                      </button>
                      <button
                          type="button"
                          data-cy="cardAttachmentsButton"
                          onClick={() => onAttachments(appeal)}
                          style={smallActionButton("#2563EB")}
                      >
                          <Paperclip size={12} strokeWidth={2.4} />
                          Attachments
                          {appeal.attachmentCount > 0 && (
                              <span
                                  style={{
                                      minWidth: 18,
                                      height: 18,
                                      padding: "0 5px",
                                      borderRadius: 999,
                                      background: "rgba(37,99,235,.12)",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 9,
                                  }}
                              >
                                  {appeal.attachmentCount}
                              </span>
                          )}
                      </button>
                      {["super admin", "admin"].includes(
                          user?.userRole?.trim().toLowerCase(),
                      ) && (
                              <>
                                  <button
                                      type="button"
                                      data-cy="cardHistoryButton"
                                      onClick={() => onHistory(appeal)}
                                      style={smallActionButton("#7C3AED")}
                                  >
                                      <History size={12} strokeWidth={2.4} />
                                      History
                                  </button>

                                  <button
                                      type="button"
                                      data-cy="cardStatusButton"
                                      onClick={() => onChangeStatus(appeal)}
                                      style={smallActionButton("#0D9488")}
                                  >
                                      <RefreshCw size={12} strokeWidth={2.4} />
                                      Status
                                  </button>
                              </>
                          )}
                  </div>
              </div>
          </div>
      </div>
    
  );
};

/* ============================================================

   INFO ROW

============================================================ */

const InfoRow = ({
  icon: Icon,

  label,

  value,

  mono = false,
}) => (
  <div
    style={{
      minWidth: 0,

      display: "flex",

      alignItems: "center",

      gap: 7,
    }}
  >
    <Icon
      size={13}

      color="#9CA3AF"

      strokeWidth={2}

      style={{ flexShrink: 0 }}
    />

    <div
      style={{
        minWidth: 0,

        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 9.5,

          color: "#9CA3AF",

          textTransform: "uppercase",

          letterSpacing: ".04em",

          fontWeight: 700,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 12,

          color: "#475569",

          marginTop: 1,

          overflow: "hidden",

          textOverflow: "ellipsis",

          whiteSpace: "nowrap",

          fontFamily: mono
            ? "ui-monospace, SFMono-Regular, Menlo, monospace"
            : "inherit",
        }}
      >
        {value || "—"}
      </div>
    </div>
  </div>
);

/* ============================================================

   STATUS COUNT CARD

============================================================ */

const StatusCountCard = ({
  label,

  count,

  status,

  active,

  onClick,
}) => {
  const meta = STATUS_META[status];

  const Icon = meta.icon;

  return (
    <button
      type="button"

      onClick={onClick}

      style={{
        border: active ? `1.5px solid ${meta.color}` : "1px solid #E8ECF0",

        background: active ? `${meta.bg}` : "#fff",

        borderRadius: 14,

        minHeight: 104,

        padding: 14,

        cursor: "pointer",

        display: "flex",

        flexDirection: "column",

        alignItems: "center",

        justifyContent: "center",

        textAlign: "center",

        fontFamily: "inherit",

        transition: "all .15s",

        boxShadow: active
          ? `0 3px 12px ${meta.color}18`
          : "0 1px 2px rgba(0,0,0,.02)",
      }}
    >
      <div
        style={{
          width: 30,

          height: 30,

          borderRadius: 9,

          display: "flex",

          alignItems: "center",

          justifyContent: "center",

          background: meta.bg,

          color: meta.color,

          marginBottom: 6,
        }}
      >
        <Icon size={16} strokeWidth={2.2} />
      </div>

      <div
        style={{
          fontSize: 21,

          lineHeight: 1,

          fontWeight: 800,

          color: "#1A1D21",
        }}
      >
        {count}
      </div>

      <div
        style={{
          fontSize: 10.5,

          fontWeight: 700,

          color: "#6B7280",

          marginTop: 5,

          lineHeight: 1.2,
        }}
      >
        {label}
      </div>
    </button>
  );
};

/* =========================================================

   Menu Item

========================================================= */

const MenuItem = ({
  icon: Icon,

  label,

  onClick,

  color = "#1A1D21",
}) => (
  <button
    onClick={onClick}

    style={{
      display: "flex",

      alignItems: "center",

      gap: 10,

      width: "100%",

      padding: "9px 12px",

      borderRadius: 8,

      border: "none",

      background: "transparent",

      fontSize: 13,

      fontWeight: 600,

      color,

      cursor: "pointer",

      fontFamily: "inherit",

      textAlign: "left",
    }}

    onMouseEnter={(e) => {
      e.currentTarget.style.background = "#F8FAFB";
    }}

    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
  >
    <Icon size={15} strokeWidth={2} />

    {label}
  </button>
);

/* ============================================================

   MAIN COMPONENT

============================================================ */

const ClaimAppealListPage = () => {
  const CLAIM_APPEAL_LIST_STATE_KEY = "claimAppealListState";

  const getInitialListState = () => {
    try {
      const saved = sessionStorage.getItem(CLAIM_APPEAL_LIST_STATE_KEY);

      if (!saved) {
        return null;
      }

      return JSON.parse(saved);
    } catch {
      return null;
    }
  };

  const toast = useToast();

  const navigate = useNavigate();
  const { user } = useAuth();
  const { can, canAny, canAll } = usePermission();

  const [appeals, setAppeals] = useState([]);
  const [fromClaimAppealList, setFromClaimAppealList] = useState(true);

  const [searchText, setSearchText] = useState("");

  const [filterStatus, setFilterStatus] = useState("All");

  const [totalRecord, setTotalRecord] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});

  const savedListState = getInitialListState();

  const [page, setPage] = useState({
    skip: 0,
    take: 10,
  });

  const [sort, setSort] = useState([]);

  const searchTimeoutRef = useRef(null);

  const [viewMode, setViewMode] = useState("cards");

  const [loading, setLoading] = useState(false);

  const [pageLoading, setPageLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);

  const [error, setError] = useState(null);

  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024,
  );

  /* Change status dialog */

  const [statusDialog, setStatusDialog] = useState(null);

  const [newStatus, setNewStatus] = useState("");

  const [statusNotes, setStatusNotes] = useState("");

  const [statusSaving, setStatusSaving] = useState(false);

  /* History dialog */

  const [historyDialog, setHistoryDialog] = useState(null);

  const [history, setHistory] = useState([]);

  const [historyLoading, setHistoryLoading] = useState(false);

  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const [showProviderDialog, setShowProviderDialog] = useState(false);

  const [providers, setProviders] = useState([]);

  const [selectedProvider, setSelectedProvider] = useState(null);

  const [providerLoading, setProviderLoading] = useState(false);

  const [providerSearch, setProviderSearch] = useState("");

  const [selectedAppealDetails, setSelectedAppealDetails] = useState(null);

  //attachment & preview
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);

  const [selectedAppeal, setSelectedAppeal] = useState(null);

  const [previewAttachment, setPreviewAttachment] = useState(null);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  /********************Access permission************/
  const canViewClaimAppealDetails = can(
    "claimAndPayments",
    "claimAppeals",
    "viewClaimAppealDetails",
  );

  const canViewClaimAppealAttachment = can(
    "claimAndPayments",
    "claimAppeals",
    "viewClaimAppealAttachment",
  );
  const canViewClaimAppealStatusHistory = can(
    "claimAndPayments",
    "claimAppeals",
    "viewClaimAppealStatusHistory",
  );

  const canUpdateClaimAppealStatus = can(
    "claimAndPayments",
    "claimAppeals",
    "updateClaimAppealStatus",
  );

  const canFileClaimAppeal = can(
    "claimAndPayments",
    "claimAppeals",
    "fileClaimAppeal",
  );
  /********************Access permission************/

  /* ========================================================

       RESPONSIVE

    ======================================================== */

  useLayoutEffect(() => {
    if (typeof window === "undefined") return;

    const onResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0, // replaces top/left/width/height
      backgroundColor: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    },
    gridOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
  };

  /* ========================================================

       GET ENROLLMENTS

    ======================================================== */

  const getAppeals = async ({
    requestedSkip = page.skip,
    requestedTake = page.take,
    requestedSearchText = searchText,
    requestedStatus = filterStatus,
    requestedSort = sort,
  } = {}) => {
    setGridLoading(true);
    setError(null);

    try {
      if (!isApiConfigured()) {
        setAppeals([]);
        setTotalRecord(0);
        setStatusCounts({});
        return;
      }

      const sortDescriptor = requestedSort?.[0];

      const res = await appealApi.listAppeals2({
        searchText: requestedSearchText,
        status: requestedStatus === "All" ? "" : requestedStatus,
        sortColumnName: sortDescriptor?.field || "",
        sortColumnDirection: sortDescriptor?.dir || "",
        startIndex: requestedSkip,
        pageSize: requestedTake,
        exportCsv: false,
      });

      const data = res?.data ?? res?.Data ?? [];

      const formattedData = data.map((e) => ({
        id: e.AppealId,
        appealId: e.AppealId,
        appealNumber: e.AppealNumber || "",

        claimId: e.ClaimId || null,
        claimNumber: e.ClaimNumber || "",

        groupId: e.GroupId || null,

        memberName: e.MemberName || "",
        memberId: e.MemberId || "",

        providerName: e.ProviderName || "",
        npi: e.NPI || "",
        tin: e.TIN || "",

        reason: e.Reason || "",
        requestedOutcome: e.RequestedOutcome || "",

        contactEmail: e.ContactEmail || "",
        contactPhone: e.ContactPhone || "",

        userId: e.UserId || "",

        note: e.Note || "",
        adminNote: e.AdminNote || "",

        status: e.Status || "Submitted",

        createdBy: e.CreatedBy || "",
        createdDate: e.CreatedDate || "",
        submissionDate: e.CreatedDate || "",

        modifiedBy: e.ModifiedBy || null,
        modifiedDate: e.ModifiedDate || null,

        attachmentCount: e.AttachmentCount || 0,

        registeredFirstName: e.RegisteredFirstName || "",
        registeredLastName: e.RegisteredLastName || "",
        registeredUserName: e.RegisteredUserName || "",
        registeredEmail: e.RegisteredEmail || "",
      }));

      setAppeals(formattedData);

      setTotalRecord(Number(res?.totalRecord ?? res?.TotalRecord ?? 0));

      const counts = {};

      STATUS_OPTIONS.forEach((status) => {
        counts[status] = 0;
      });

      const apiStatusCounts = res?.statusCounts ?? res?.StatusCounts ?? [];

      apiStatusCounts.forEach((item) => {
        const status = item.Status;
        const count = item.StatusCount ?? item.Count ?? 0;

        counts[status] = Number(count);
      });

      setStatusCounts(counts);
    } catch (err) {
      setError(err?.message || "Failed to load claim appeals");
    } finally {
      setGridLoading(false);
    }
  };

  //useEffect(() => {
  //  getAppeals({
  //    requestedSkip: 0,
  //    requestedTake: page.take,
  //    requestedSearchText: "",
  //    requestedStatus: "All",
  //    requestedSort: [],
  //  });
  //}, []);

  useEffect(() => {
    const savedState = sessionStorage.getItem(CLAIM_APPEAL_LIST_STATE_KEY);

    let initialSearchText = "";
    let initialFilterStatus = "All";
    let initialPage = {
      skip: 0,
      take: 10,
    };
    let initialSort = [];

    let initialTotalRecord = 0;
    let initialViewMode = "cards";

    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);

        initialSearchText = parsed.searchText ?? "";
        initialFilterStatus = parsed.filterStatus ?? "All";
        initialTotalRecord = parsed.totalRecord ?? 0;
        initialViewMode = parsed.viewMode ?? "cards";
        initialPage = parsed.page ?? {
          skip: 0,
          take: 10,
        };
        initialSort = parsed.sort ?? [];

        setSearchText(initialSearchText);
        setFilterStatus(initialFilterStatus);
        setPage(initialPage);
        setSort(initialSort);
        setTotalRecord(initialTotalRecord);
        setViewMode(initialViewMode);
      } catch (error) {
        console.error("Failed to restore EFT list state:", error);
      }
    }

    //  alert("Make API CALL");
    getAppeals({
      requestedSkip: initialPage.skip,
      requestedTake: initialPage.take,
      requestedSearchText: initialSearchText,
      requestedStatus: initialFilterStatus,
      requestedSort: initialSort,
    });

    sessionStorage.removeItem(CLAIM_APPEAL_LIST_STATE_KEY);
  }, []);

  const saveListState = () => {
    sessionStorage.setItem(
      CLAIM_APPEAL_LIST_STATE_KEY,
      JSON.stringify({
        searchText,
        filterStatus,
        page,
        sort,
        viewMode,
        totalRecord,
      }),
    );
  };

  const ProviderAvatar = ({ name = "", size = 44 }) => (
    <div
      style={{
        width: size,

        height: size,

        borderRadius: size * 0.32,

        background:
          "linear-gradient(135deg, rgba(13,148,136,0.14), rgba(13,148,136,0.05))",

        color: "#0D9488",

        display: "flex",

        alignItems: "center",

        justifyContent: "center",

        fontSize: size * 0.34,

        fontWeight: 700,

        border: "1.5px solid rgba(13,148,136,0.18)",

        flexShrink: 0,
      }}
    >
      {getProviderInitials(name)}
    </div>
  );

  const getProviderInitials = (name = "") => {
    const words = name

      .trim()

      .split(/\s+/)

      .filter(Boolean);

    if (!words.length) return "?";

    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }

    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  };

  const handlePageChange = (event) => {
    const newPage = event.page || event;

    setPage({
      skip: newPage.skip,
      take: newPage.take,
    });

    getAppeals({
      requestedSkip: newPage.skip,
      requestedTake: newPage.take,
      requestedSearchText: searchText,
      requestedStatus: filterStatus,
      requestedSort: sort,
    });
  };

  const handleSortChange = (event) => {
    const nextSort = event.sort || [];

    setSort(nextSort);

    setPage((prev) => ({
      ...prev,
      skip: 0,
    }));

    getAppeals({
      requestedSkip: 0,
      requestedTake: page.take,
      requestedSearchText: searchText,
      requestedStatus: filterStatus,
      requestedSort: nextSort,
    });
  };

  //attachment and preview handler
  const handleOpenAttachments = (appeal) => {
    setSelectedAppeal(appeal);
    setAttachmentDialogOpen(true);
  };

  const handleCloseAttachments = () => {
    setAttachmentDialogOpen(false);
    setSelectedAppeal(null);
  };

  const handlePreviewAttachment = (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewAttachment(null);
  };

  /* ========================================================

       CHANGE STATUS

    ======================================================== */

  const openStatusDialog = (enrollment) => {
    setStatusDialog(enrollment);

    setNewStatus(enrollment.status || "Submitted");

    setStatusNotes("");
  };

  const closeStatusDialog = () => {
    if (statusSaving) return;

    setStatusDialog(null);

    setNewStatus("");

    setStatusNotes("");
  };

  const handleStatusSave = async () => {
    if (!statusDialog) return;

    if (!newStatus) {
      toast.error(
        "Please select a status.",

        {
          title: "Status required",
        },
      );

      return;
    }

    if (newStatus === statusDialog.status && !statusNotes.trim()) {
      toast.info("Select a different status or enter notes.", {
        title: "No changes",
      });

      return;
    }

    const id = statusDialog.appealId || statusDialog.id;

    setStatusSaving(true);

    //alert('id : ' + id + ' newSatus : ' + newStatus + ' statusNotes : ' + statusNotes);
    //return;

    try {
      const response = await appealApi.updateClaimAppealStatus({
        id,
        newStatus,
        statusNotes: statusNotes.trim(),
      });

      //if (response?.success === false || response?.Success === false) {
      //  throw new Error(
      //    response?.message ||
      //      response?.Message ||
      //      "Failed to update Claim Appeal status.",
      //  );
      //}

      if (response?.success === false || response?.Success === false) {
        const errors = response?.data?.errors || response?.Data?.errors;

        let message =
          response?.message ||
          response?.Message ||
          "Failed to update Claim Appeal status.";

        if (errors) {
          const firstError = Object.values(errors)
            .flat()
            .find((error) => error);

          if (firstError) {
            message = firstError;
          }
        }

        toast.warning(
          message,

          {
            title: "Status updated",
          },
        );
        return;
      }

      toast.success(
        `${getClaimAppealName(statusDialog)} 's claim appeal is now ${newStatus}.`,

        {
          title: "Status updated",
        },
      );

      await getAppeals({
        requestedSkip: page.skip,
        requestedTake: page.take,
        requestedSearchText: searchText,
        requestedStatus: filterStatus,
        requestedSort: sort,
      });

      closeStatusDialog();
    } catch (err) {
      console.error(
        "Claim Appeal status update error:",

        err,
      );

      //toast.error(
      //  err.message || "Failed to update Claim Appeal status.",

      //  {
      //    title: "Status change failed",
      //  },
      //);

      const res = err?.response?.data;

      const errors = res?.Data?.errors;
      let message = res?.Message || "Validation failed";

      if (errors) {
        const firstError = Object.values(errors)
          .flat()
          .find((error) => error);

        if (firstError) {
          message = firstError;
        }
      }

      toast.warning(message, {
        title: "Claim Status Change",
      });
    } finally {
      setStatusSaving(false);
    }
  };

  /* DEtails dialog */

  const openDetails = (appeal) => {
    setSelectedAppealDetails(appeal);

    setShowDetailsDialog(true);
  };

  const closeDetails = () => {
    setSelectedAppeal(null);
    setSelectedAppealDetails(null);

    setShowDetailsDialog(false);
  };

  /* ========================================================

       STATUS HISTORY

    ======================================================== */

  const openHistoryDialog = async (appeal) => {
    setHistoryDialog(appeal);

    setHistory([]);

    setHistoryLoading(true);

    try {
      const id = appeal.appealId || appeal.id;

      const response = await appealApi.getStatusHistory(id);

      const data = response?.data ?? response?.Data ?? [];

      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(
        "History error:",

        err,
      );

      toast.error(
        err.message || "Failed to load status history.",

        {
          title: "History unavailable",
        },
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryDialog = () => {
    if (historyLoading) return;

    setHistoryDialog(null);

    setHistory([]);
  };

  /* ========================================================
       TABLE CELLS
    ======================================================== */

  const AppealCell = (props) => {
    const item = props.dataItem;

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "rgba(13,148,136,.08)",
              color: "#0D9488",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FileText size={16} strokeWidth={2} />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: "#1A1D21",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.appealNumber || "—"}
            </span>

            <span
              style={{
                fontSize: 10.5,
                color: "#9CA3AF",
                marginTop: 2,
              }}
            >
              Appeal ID #{item.appealId || item.id || "—"}
            </span>
          </div>
        </div>
      </td>
    );
  };

  const ClaimCell = (props) => {
    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
            }}
          >
            <Link
              name="linkClaimDetailsCard"
              state={{
                fromClaimAppealList,
              }}

              onClick={(e) => {
                e.preventDefault();
                saveListState();
                navigate(
                  `/claims/${props.dataItem.claimNumber}/null/${props.dataItem.tin}`,
                  {
                    state: {
                      fromClaimAppealList: true,
                    },
                  },
                );
              }}
              to="#"

              /*to={`/claims/${props.dataItem.claimNumber}/null/${props.dataItem.tin}`}*/

              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#0D9488",
                textDecoration: "none",
                fontFamily: "monospace",
              }}
              onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
              onMouseOut={(e) => (e.target.style.textDecoration = "none")}
            >
              {props.dataItem.claimNumber || "—"}
            </Link>
            {/*{props.dataItem.claimNumber || "—"}*/}
          </span>

          {/*<span*/}
          {/*    style={{*/}
          {/*        fontSize: 10.5,*/}
          {/*        color: "#9CA3AF",*/}
          {/*        fontFamily:*/}
          {/*            "ui-monospace, SFMono-Regular, Menlo, monospace",*/}
          {/*    }}*/}
          {/*>*/}
          {/*    ID: {props.dataItem.claimId || "—"}*/}
          {/*</span>*/}
        </div>
      </td>
    );
  };

  const MemberCell = (props) => {
    const item = props.dataItem;

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.memberName || "—"}
          </span>

          <span
            style={{
              fontSize: 10.5,
              color: "#9CA3AF",
            }}
          >
            ID: {item.memberId || "—"}
          </span>
        </div>
      </td>
    );
  };

  const ProviderCell = (props) => {
    const item = props.dataItem;

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: "rgba(13,148,136,.08)",
              color: "#0D9488",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Building2 size={16} strokeWidth={2} />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: "#1A1D21",
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.providerName || "—"}
            </span>

            <span
              style={{
                fontSize: 10.5,
                color: "#9CA3AF",
                marginTop: 2,
              }}
            >
              {item?.registeredUserName ? "@" + item?.registeredUserName : ""}{" "}
              {item?.registeredEmail || ""}
            </span>
          </div>
        </div>
      </td>
    );
  };

  const TINCell = (props) => {
    return (
      <td
        {...props.tdProps}
        colSpan={props.colSpan}
        style={{
          ...props.style,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#475569",
        }}
      >
        {props.dataItem.tin || "—"}
      </td>
    );
  };

  const NPICell = (props) => {
    return (
      <td
        {...props.tdProps}
        colSpan={props.colSpan}
        style={{
          ...props.style,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#475569",
        }}
      >
        {props.dataItem.npi || "—"}
      </td>
    );
  };

  const ContactEmailCell = (props) => {
    const email = props.dataItem.contactEmail;

    return (
      <td
        {...props.tdProps}
        colSpan={props.colSpan}
        style={{
          ...props.style,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#475569",
        }}
      >
        {email ? (
          <a
            href={`mailto:${email}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "#475569",
              textDecoration: "none",
            }}
            title={`Email ${email}`}
          >
            <Mail size={14} strokeWidth={1.8} color="#64748b" />

            <span>{email}</span>
          </a>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Mail size={14} strokeWidth={1.8} color="#94a3b8" />
            <span>—</span>
          </span>
        )}
      </td>
    );
  };

  const ContactPhoneCell = (props) => {
    const phone = props.dataItem.contactPhone;

    return (
      <td
        {...props.tdProps}
        colSpan={props.colSpan}
        style={{
          ...props.style,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 12,
          color: "#475569",
        }}
      >
        {phone ? (
          <a
            href={`tel:${phone}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "#475569",
              textDecoration: "none",
            }}
            title={`Call ${phone}`}
          >
            <Phone size={14} strokeWidth={1.8} color="#64748b" />

            <span>{phone}</span>
          </a>
        ) : (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Phone size={14} strokeWidth={1.8} color="#94a3b8" />
            <span>—</span>
          </span>
        )}
      </td>
    );
  };

  const StatusCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <StatusBadge status={props.dataItem.status} />
    </td>
  );

  const DateCell = (props) => (
    <td
      {...props.tdProps}
      colSpan={props.colSpan}
      style={{
        ...props.style,
        color: "#6B7280",
        fontSize: 12,
      }}
    >
      {formatDate(props.dataItem.submissionDate)}
    </td>
  );

  const RequestedOutcomeCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <span
        title={props.dataItem.requestedOutcome || ""}
        style={{
          display: "block",
          maxWidth: 210,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 12,
          color: "#475569",
        }}
      >
        {props.dataItem.requestedOutcome || "—"}
      </span>
    </td>
  );

  const NoteCell = (props) => (
    <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
      <span
        title={props.dataItem.adminNote || ""}
        style={{
          display: "block",
          maxWidth: 220,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontSize: 12,
          color: "#64748B",
        }}
      >
        {props.dataItem.adminNote || "—"}
      </span>
    </td>
  );

  const RegisteredUserCell = (props) => {
    const item = props.dataItem;

    const fullName = [item.registeredFirstName, item.registeredLastName]
      .filter(Boolean)
      .join(" ");

    return (
      <td {...props.tdProps} colSpan={props.colSpan} style={props.style}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#374151",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.registeredUserName ? `@${item.registeredUserName} ` : "—"}
          </span>

          <span
            style={{
              fontSize: 11,
              color: "#64748B",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fullName || "—"}
          </span>

          <span
            title={item.registeredEmail || ""}
            style={{
              fontSize: 10.5,
              color: "#9CA3AF",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.registeredEmail || "—"}
          </span>
        </div>
      </td>
    );
  };

  const handleAppealAction = (appealId, claimNumber, userId) => {
    //alert(userId);
    //return;

    saveListState();

    navigate(
      //    `/claims/${encodeURIComponent(claimNumber)}/appeal?aid=${encodeURIComponent(appealId)}&uid=${encodeURIComponent(userId)}`
      `/claims/${encodeURIComponent(claimNumber)}/appeal/${encodeURIComponent(appealId)}/${encodeURIComponent(userId)}`,
    );
  };

  const loadProviderAdmins = async (search = "") => {
    try {
      setProviderLoading(true);

      const response = await eftEnrollmentApi.getProviderAdmins({
        search: search,

        first: 0,

        max: 20,
      });

      //console.log(

      //    'PROVIDER ADMINS RESPONSE:',

      //    response

      //);

      if (response?.success) {
        const providerList = Array.isArray(response.data) ? response.data : [];

        const formattedProviders = providerList

          .filter((provider) => provider?.Id)

          .map((provider) => ({
            id: String(provider.Id),

            username: provider.Username || "",

            firstName: provider.FirstName || "",

            lastName: provider.LastName || "",

            email: provider.Email || "",

            displayName:
              [provider.FirstName, provider.LastName]

                .filter(Boolean)

                .join(" ") ||
              provider.Username ||
              "Unknown Provider",
          }));

        //console.log(

        //    'FORMATTED PROVIDERS:',

        //    formattedProviders

        //);

        setProviders(formattedProviders);
      } else {
        setProviders([]);
      }
    } catch (error) {
      console.error(
        "Failed to load provider admins:",

        error,
      );

      setProviders([]);
    } finally {
      setProviderLoading(false);
    }
  };

  const handleProviderContinue = () => {
    if (!selectedProvider?.id) {
      toast.warning(
        "Please select a provider before continuing.",

        {
          title: "Provider required",
        },
      );
      return;
    }

    console.log("Selected Provider:", selectedProvider);

    console.log("Selected Provider Keycloak ID:", selectedProvider.id);

    setShowProviderDialog(false);

    saveListState();

    navigate(`/claims`, {
      state: {
        selectedUserId: selectedProvider.id,
      },
    });

    //navigate(
    //  `/forms/era-enrollment?uid=${encodeURIComponent(
    //    selectedProvider.id,
    //  )}&mode=newEnrollment`,
    //);
  };

  const onFileAppeal = async () => {
    setSelectedProvider(null);

    setProviderSearch("");

    setShowProviderDialog(true);

    await loadProviderAdmins("");
  };

  const handleProviderFilterChange = (searchValue) => {
    setProviderSearch(searchValue);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadProviderAdmins(searchValue);
    }, 300);
  };

  const ActionsCell = (props) => {
    const item = props.dataItem;

    return (
      <td
        {...props.tdProps}

        colSpan={props.colSpan}

        style={props.style}
      >
        <div
          style={{
            display: "flex",

            gap: 6,
          }}
        >
          <button
                    onClick={() => openDetails(props.dataItem)}
                    name="btnViewClaimAppealDetailsGrid"

            title="View Claim Appeal"

            style={iconBtn("#0D9488")}
          >
            <Eye
              size={13}

              strokeWidth={2.5}
            />
          </button>
          {/* View Attachments */}
          <button
            type="button"
                    onClick={() => handleOpenAttachments(item)}
                    name="btnOpenClaimAttachmentsGrid"
            title={`View Attachments (${item.attachmentCount ?? 0})`}
            style={{
              width: 32,
              height: 32,
              padding: 0,
              borderRadius: 8,
              border: "1px solid rgba(13,148,136,.20)",
              background: "rgba(13,148,136,.06)",
              color: "#0D9488",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <Paperclip size={14} strokeWidth={2.2} />

            {/* Attachment count */}
            {(item.attachmentCount ?? 0) > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  minWidth: 15,
                  height: 15,
                  padding: "0 3px",
                  borderRadius: 10,
                  background: "#0D9488",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  border: "2px solid #fff",
                }}
              >
                {item.attachmentCount}
              </span>
            )}
          </button>

          <button
            onClick={() => {
              //handleClaimAppealAction(
              //  props.dataItem.claimNumber,
              //  props.dataItem.appealId,
              //);
              handleAppealAction(
                props.dataItem.appealId,
                props.dataItem.claimNumber,
                props.dataItem.userId,
              );
            }}

                    title="Edit Claim Appeal"
            name="btnEditClaimAppealGrid"

            style={iconBtn("#2563EB")}
          >
            <Edit2
              size={13}

              strokeWidth={2.5}
            />
          </button>

          {["super admin", "admin"].includes(
            user?.userRole?.trim().toLowerCase(),
          ) && (
            <>
              <button
                            type="button"
                            name="btnViewStatusHistoryGrid" 
                

                onClick={() => openHistoryDialog(item)}

                title="View status history"

                style={iconBtn("#7C3AED")}
              >
                <History
                  size={13}

                  strokeWidth={2.5}
                />
              </button>
              <button
                type="button"

                            onClick={() => openStatusDialog(item)}
                name="btnUpdateStatusGrid"

                title="Change Claim Appeal status"

                style={iconBtn("#0D9488")}
              >
                <RefreshCw
                  size={13}

                  strokeWidth={2.5}
                />
              </button>
            </>
          )}
        </div>
      </td>
    );
  };

  /* ========================================================

       RENDER

    ======================================================== */

  return (
    <div className="main-content">
      {pageLoading && (
        <div style={styles.overlay}>
          <LoadingSpinner
            message="Please wait..."

            fullPage="true"
          />
        </div>
      )}

      {/* ==================================================

                HEADER

            ================================================== */}

      <div className="page-header">
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 4,
            }}
          >
            <FileText size={22} color="#0D9488" strokeWidth={2} />

            <h1 style={{ margin: 0 }}>Claim Appeals</h1>
          </div>

          <p>
            Manage claim appeals, review appeal details, track statuses, and
            handle supporting documentation
          </p>
        </div>
      </div>

      {/* ==================================================

                STATUS CARDS

            ================================================== */}

      <div
        style={{
          display: "grid",

          gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))",

          gap: 12,

          marginBottom: 20,
        }}
      >
        {STATUS_OPTIONS.map((status) => (
          <StatusCountCard
            key={status}

            label={status}

            status={status}

            count={statusCounts[status] || 0}

            active={filterStatus === status}

            onClick={() => {
              setFilterStatus(status);

              setPage((prev) => ({
                ...prev,
                skip: 0,
              }));

              getAppeals({
                requestedSkip: 0,
                requestedTake: page.take,
                requestedSearchText: searchText,
                requestedStatus: status,
                requestedSort: sort,
              });
            }}
          />
        ))}
      </div>

      {/* ==================================================

                TOOLBAR

            ================================================== */}
      <div style={{ position: "relative" }}>
        {gridLoading && (
          <div style={styles.gridOverlay}>
            <LoadingSpinner
              data-cy="grid-loader"
              message="Loading Claim Appeals..."
            />
          </div>
        )}
        <div
          className="card"

          style={{
            marginBottom: 20,
          }}
        >
          {/*File Claim Appeal Button */}

          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            {/*{user?.userRole?.trim().toLowerCase() === "super admin" && (*/}
            {["super admin", "admin"].includes(
              user?.userRole?.trim().toLowerCase(),
            ) && (
              <>
                <button
                  type="button"
                                  onClick={onFileAppeal}
                  name="btnFileClaimAppeal"
                  style={{
                    padding: "9px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: "#0D9488",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 7,
                    fontFamily: "inherit",
                    boxShadow: "0 2px 5px rgba(13, 148, 136, 0.25)",
                  }}
                >
                  <Plus size={15} strokeWidth={2.5} />
                  File Claim Appeal
                </button>
              </>
            )}
          </div>

          {/* Search / Status Filter / View Toggle */}

          <div
            style={{
              display: "flex",

              gap: 12,

              alignItems: "center",

              flexWrap: "wrap",

              width: "100%",
            }}
          >
            {/* Search */}

            <div
              style={{
                position: "relative",

                flex: "1 1 300px",

                minWidth: 220,
              }}
            >
              <span
                style={{
                  position: "absolute",

                  left: 14,

                  top: "50%",

                  transform: "translateY(-50%)",

                  color: "#9CA3AF",

                  display: "flex",
                }}
              >
                <Search size={16} />
              </span>

              <input
                type="search"

                value={searchText}

                onChange={(e) => {
                  const value = e.target.value;

                  setSearchText(value);

                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }

                  searchTimeoutRef.current = setTimeout(() => {
                    setPage((prev) => ({
                      ...prev,
                      skip: 0,
                    }));

                    getAppeals({
                      requestedSkip: 0,
                      requestedTake: page.take,
                      requestedSearchText: value,
                      requestedStatus: filterStatus,
                      requestedSort: sort,
                    });
                  }, 400);
                }}

                placeholder="Search provider, TIN, NPI, email, Retrieval method...."

                style={{
                  width: "100%",

                  padding: "10px 14px 10px 40px",

                  borderRadius: 10,

                  border: "1.5px solid #E8ECF0",

                  fontSize: 14,

                  fontFamily: "inherit",

                  background: "#F8FAFB",

                  outline: "none",

                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Status Filter */}

            <div
              style={{
                display: "flex",

                gap: 8,

                alignItems: "center",
              }}
            >
              <Filter
                size={15}

                color="#9CA3AF"
              />

              <select
                value={filterStatus}
                name="selStatus"
                onChange={(e) => {
                  const value = e.target.value || "All";

                  setFilterStatus(value);

                  setPage((prev) => ({
                    ...prev,
                    skip: 0,
                  }));

                  getAppeals({
                    requestedSkip: 0,
                    requestedTake: page.take,
                    requestedSearchText: searchText,
                    requestedStatus: value,
                    requestedSort: sort,
                  });
                }}

                style={selectStyle}
              >
                <option value="All">All Statuses</option>

                {STATUS_OPTIONS.map((status) => (
                  <option
                    key={status}

                    value={status}
                  >
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* View Toggle */}

            <div
              style={{
                display: "flex",

                background: "#F1F5F9",

                borderRadius: 10,

                padding: 3,

                gap: 2,
              }}
            >
              <button
                type="button"

                onClick={() => setViewMode("cards")}

                style={toggleBtnStyle(viewMode === "cards")}
              >
                <LayoutGrid
                  size={14}

                  strokeWidth={2.5}
                />
                Cards
              </button>

              <button
                type="button"

                onClick={() => setViewMode("table")}

                style={toggleBtnStyle(viewMode === "table")}
              >
                <List
                  size={14}

                  strokeWidth={2.5}
                />
                Table
              </button>
            </div>
          </div>

          {/* Filter Summary */}

          {/*{(searchText || filterStatus !== "All") && (*/}
            <div
              style={{
                marginTop: 12,

                paddingTop: 12,

                borderTop: "1px solid #F1F5F9",

                fontSize: 13,

                color: "#6B7280",

                display: "flex",

                alignItems: "center",

                gap: 8,

                flexWrap: "wrap",
              }}
            >
              <span>
                Showing{" "}
                <strong style={{ color: "#1A1D21" }}>{appeals.length}</strong>{" "}
                of {totalRecord} appeals
              </span>
                      {(searchText || filterStatus !== "All") && (
              <button
                type="button"

                onClick={() => {
                  setSearchText("");
                  setFilterStatus("All");

                  sessionStorage.removeItem(CLAIM_APPEAL_LIST_STATE_KEY);

                  setPage((prev) => ({
                    ...prev,
                    skip: 0,
                  }));

                  setSort([]);

                  getAppeals({
                    requestedSkip: 0,
                    requestedTake: page.take,
                    requestedSearchText: "",
                    requestedStatus: "All",
                    requestedSort: [],
                  });
                }}

                style={{
                  background: "none",

                  border: "none",

                  color: "#0D9488",

                  fontWeight: 600,

                  fontSize: 13,

                  cursor: "pointer",

                  fontFamily: "inherit",

                  padding: 0,
                }}
              >
                Clear filters
                      </button>
                      )}
            </div>
          {/*)}*/}
        </div>

        {/* ==================================================

                BODY

            ================================================== */}

        {/*{loading && <LoadingSpinner message="Loading Claims Appeals..." />}*/}

        {error && (
          <ErrorCard
            message={error}

            onRetry={getAppeals}
          />
        )}

        {!loading && !error && appeals.length === 0 && (
          <EmptyState hasFilters={!!searchText || filterStatus !== "All"} />
        )}

        {!loading && !error && appeals.length > 0 && viewMode === "cards" && (
          <>
            <div
              style={{
                display: "grid",

                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",

                gap: 16,
              }}
            >
              {appeals.map((appeal) => (
                <AppealCard
                  key={appeal.id}

                  appeal={appeal}
                  user={user}

                  onChangeStatus={openStatusDialog}

                  onHistory={openHistoryDialog}
                  onView={openDetails}
                  onAttachments={handleOpenAttachments}
                  onEdit={handleAppealAction}
                  onSavedState={saveListState}
                />
              ))}
            </div>

            <CardsPagination
              skip={page.skip}

              take={page.take}

              total={totalRecord}

              onPageChange={handlePageChange}
            />
          </>
        )}

        {!loading && !error && appeals.length > 0 && viewMode === "table" && (
          <div
            style={{
              overflow: "hidden",

              borderRadius: 16,
            }}
          >
            <div className="grid-scroll-wrapper">
              <Grid
                style={{
                  height: "560px",
                }}
                data={appeals}
                skip={page.skip}
                take={page.take}
                total={totalRecord}
                pageable={{
                  pageSizes: [5, 10, 20],
                }}
                sortable
                resizable
                sort={sort}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
              >
                <GridColumn
                  field="appealNumber"
                  title="Appeal"
                  width="200px"
                  cells={{
                    data: AppealCell,
                  }}
                />

                <GridColumn
                  field="claimNumber"
                  title="Claim"
                  width="170px"
                  cells={{
                    data: ClaimCell,
                  }}
                />

                <GridColumn
                  field="memberName"
                  title="Member"
                  width="190px"
                  cells={{
                    data: MemberCell,
                  }}
                />

                <GridColumn
                  field="providerName"
                  title="Provider"
                  width="250px"
                  cells={{
                    data: ProviderCell,
                  }}
                />

                <GridColumn
                  field="npi"
                  title="NPI"
                  width="130px"
                  cells={{
                    data: NPICell,
                  }}
                />

                <GridColumn
                  field="contactEmail"
                  title="Contact Email"
                  width="250px"
                  cells={{
                    data: ContactEmailCell,
                  }}
                />

                <GridColumn
                  field="contactPhone"
                  title="Contact Phone"
                  width="180px"
                  cells={{
                    data: ContactPhoneCell,
                  }}
                />

                <GridColumn
                  field="tin"
                  title="TIN"
                  width="130px"
                  cells={{
                    data: TINCell,
                  }}
                />

                <GridColumn
                  field="status"
                  title="Status"
                  width="160px"
                  cells={{
                    data: StatusCell,
                  }}
                />

                <GridColumn
                  field="submissionDate"
                  title="Submission Date"
                  filter="date"
                  width="155px"
                  cells={{
                    data: DateCell,
                  }}
                />

                <GridColumn
                  field="requestedOutcome"
                  title="Requested Outcome"
                  width="220px"
                  cells={{
                    data: RequestedOutcomeCell,
                  }}
                />

                <GridColumn
                  field="adminNote"
                  title="Admin Note"
                  width="230px"
                  cells={{
                    data: NoteCell,
                  }}
                />

                {/*<GridColumn*/}
                {/*  field="registeredUserInfo"*/}
                {/*  title="User"*/}
                {/*  width="230px"*/}
                {/*  cells={{*/}
                {/*    data: RegisteredUserCell,*/}
                {/*  }}*/}
                {/*/>*/}

                <GridColumn
                  title="Actions"
                  width="150px"
                  cells={{
                    data: ActionsCell,
                  }}
                />
              </Grid>
            </div>
          </div>
        )}
      </div>

      {/* ==================================================

                CHANGE STATUS DIALOG

            ================================================== */}

      {statusDialog && (
        <Dialog
          title="Change Claim Appeal Status"
          onClose={closeStatusDialog}
          width={viewportWidth < 640 ? Math.max(viewportWidth - 24, 300) : 520}
        >
          <div
            style={{
              padding: "8px 0",
            }}
          >
            {/* Appeal Summary */}
            <div
              style={{
                padding: 14,
                borderRadius: 12,
                background: "rgba(13,148,136,.05)",
                border: "1px solid rgba(13,148,136,.12)",
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1A1D21",
                  wordBreak: "break-word",
                }}
              >
                {statusDialog.appealNumber || "Claim Appeal"}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginTop: 7,
                  flexWrap: "wrap",
                }}
              >
                <StatusBadge status={statusDialog.status} />

                <span
                  style={{
                    fontSize: 11,
                    color: "#9CA3AF",
                    fontFamily: "monospace",
                  }}
                >
                  Appeal #{statusDialog.appealNumber || "—"}
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color: "#9CA3AF",
                  }}
                >
                  Claim #{statusDialog.claimNumber || "—"}
                </span>
              </div>

              {(statusDialog.memberName || statusDialog.providerName) && (
                <div
                  style={{
                    display: "flex",
                    gap: 14,
                    flexWrap: "wrap",
                    marginTop: 8,
                    fontSize: 11,
                    color: "#6B7280",
                  }}
                >
                  {statusDialog.memberName && (
                    <span>
                      Member: <strong>{statusDialog.memberName}</strong>
                    </span>
                  )}

                  {statusDialog.providerName && (
                    <span>
                      Provider: <strong>{statusDialog.providerName}</strong>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Status */}
            <div
              className="form-group"
              style={{
                marginBottom: 18,
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: 7,
                }}
              >
                New Status
              </label>

              <select
                value={newStatus}
                data-cy="claimAppealStatusSelect"
                onChange={(e) => setNewStatus(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1.5px solid #E8ECF0",
                  borderRadius: 10,
                  background: "#F8FAFB",
                  fontSize: 14,
                  fontFamily: "inherit",
                  outline: "none",
                }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="form-group">
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#6B7280",
                  marginBottom: 7,
                }}
              >
                Admin Notes
              </label>

              <textarea
                value={statusNotes}
                data-cy="claimAppealStatusNotes"
                onChange={(e) => setStatusNotes(e.target.value)}
                rows={5}
                maxLength={1000}
                placeholder="Enter reason, review notes, or additional information..."
                style={{
                  width: "100%",
                  resize: "vertical",
                  padding: "10px 12px",
                  border: "1.5px solid #E8ECF0",
                  borderRadius: 10,
                  background: "#F8FAFB",
                  fontSize: 14,
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />

              <div
                style={{
                  textAlign: "right",
                  fontSize: 11,
                  color: "#9CA3AF",
                  marginTop: 5,
                }}
              >
                {statusNotes.length}/1000
              </div>
            </div>
          </div>

          <DialogActionsBar>
            <Button
              data-cy="claimAppealStatusChangeCloseButton"
              onClick={closeStatusDialog}
              disabled={statusSaving}
            >
              Cancel
            </Button>

            <Button
              themeColor="primary"
              onClick={handleStatusSave}
              disabled={statusSaving || !newStatus}
              data-cy="claimAppealStatusUpdateButton"
            >
              {statusSaving ? "Updating..." : "Update Status"}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/*{statusDialog && (*/}
      {/*  <Dialog*/}
      {/*    title="Change ERA Enrollment Status"*/}

      {/*    onClose={closeStatusDialog}*/}

      {/*    width={*/}
      {/*      viewportWidth < 640*/}
      {/*        ? Math.max(*/}
      {/*            viewportWidth - 24,*/}

      {/*            300,*/}
      {/*          )*/}
      {/*        : 520*/}
      {/*    }*/}
      {/*  >*/}
      {/*    <div*/}
      {/*      style={{*/}
      {/*        padding: "8px 0",*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      */}
      {/* Provider summary */}

      {/*      <div*/}
      {/*        style={{*/}
      {/*          padding: 14,*/}

      {/*          borderRadius: 12,*/}

      {/*          background: "rgba(13,148,136,.05)",*/}

      {/*          border: "1px solid rgba(13,148,136,.12)",*/}

      {/*          marginBottom: 18,*/}
      {/*        }}*/}
      {/*      >*/}
      {/*        <div*/}
      {/*          style={{*/}
      {/*            fontSize: 15,*/}

      {/*            fontWeight: 700,*/}

      {/*            color: "#1A1D21",*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          {getEnrollmentName(statusDialog)}*/}
      {/*        </div>*/}

      {/*        <div*/}
      {/*          style={{*/}
      {/*            display: "flex",*/}

      {/*            gap: 8,*/}

      {/*            alignItems: "center",*/}

      {/*            marginTop: 7,*/}

      {/*            flexWrap: "wrap",*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          <StatusBadge status={statusDialog.status} />*/}

      {/*          <span*/}
      {/*            style={{*/}
      {/*              fontSize: 11,*/}

      {/*              color: "#9CA3AF",*/}

      {/*              fontFamily: "monospace",*/}
      {/*            }}*/}
      {/*          >*/}
      {/*            Enrollment #{statusDialog.id}*/}
      {/*          </span>*/}
      {/*        </div>*/}
      {/*      </div>*/}

      {/*      */}
      {/* Status */}

      {/*      <div*/}
      {/*        className="form-group"*/}

      {/*        style={{*/}
      {/*          marginBottom: 18,*/}
      {/*        }}*/}
      {/*      >*/}
      {/*        <label*/}
      {/*          style={{*/}
      {/*            display: "block",*/}

      {/*            fontSize: 13,*/}

      {/*            fontWeight: 600,*/}

      {/*            color: "#6B7280",*/}

      {/*            marginBottom: 7,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          New Status*/}
      {/*        </label>*/}

      {/*        <select*/}
      {/*          value={newStatus}*/}

      {/*          onChange={(e) => setNewStatus(e.target.value)}*/}

      {/*          style={{*/}
      {/*            width: "100%",*/}

      {/*            padding: "10px 12px",*/}

      {/*            border: "1.5px solid #E8ECF0",*/}

      {/*            borderRadius: 10,*/}

      {/*            background: "#F8FAFB",*/}

      {/*            fontSize: 14,*/}

      {/*            fontFamily: "inherit",*/}

      {/*            outline: "none",*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          {STATUS_OPTIONS.map((status) => (*/}
      {/*            <option*/}
      {/*              key={status}*/}

      {/*              value={status}*/}
      {/*            >*/}
      {/*              {status}*/}
      {/*            </option>*/}
      {/*          ))}*/}
      {/*        </select>*/}
      {/*      </div>*/}

      {/*      */}
      {/* Notes */}

      {/*      <div className="form-group">*/}
      {/*        <label*/}
      {/*          style={{*/}
      {/*            display: "block",*/}

      {/*            fontSize: 13,*/}

      {/*            fontWeight: 600,*/}

      {/*            color: "#6B7280",*/}

      {/*            marginBottom: 7,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          Admin Notes*/}
      {/*        </label>*/}

      {/*        <textarea*/}
      {/*          value={statusNotes}*/}

      {/*          onChange={(e) => setStatusNotes(e.target.value)}*/}

      {/*          rows={5}*/}

      {/*          maxLength={1000}*/}

      {/*          placeholder="Enter reason, review notes, or additional information..."*/}

      {/*          style={{*/}
      {/*            width: "100%",*/}

      {/*            resize: "vertical",*/}

      {/*            padding: "10px 12px",*/}

      {/*            border: "1.5px solid #E8ECF0",*/}

      {/*            borderRadius: 10,*/}

      {/*            background: "#F8FAFB",*/}

      {/*            fontSize: 14,*/}

      {/*            fontFamily: "inherit",*/}

      {/*            outline: "none",*/}

      {/*            boxSizing: "border-box",*/}
      {/*          }}*/}
      {/*        />*/}

      {/*        <div*/}
      {/*          style={{*/}
      {/*            textAlign: "right",*/}

      {/*            fontSize: 11,*/}

      {/*            color: "#9CA3AF",*/}

      {/*            marginTop: 5,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          {statusNotes.length}/1000*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*    </div>*/}

      {/*    <DialogActionsBar>*/}
      {/*      <Button*/}
      {/*        onClick={closeStatusDialog}*/}

      {/*        disabled={statusSaving}*/}
      {/*      >*/}
      {/*        Cancel*/}
      {/*      </Button>*/}

      {/*      <Button*/}
      {/*        themeColor="primary"*/}

      {/*        onClick={handleStatusSave}*/}

      {/*        disabled={statusSaving || !newStatus}*/}
      {/*      >*/}
      {/*        {statusSaving ? "Updating..." : "Update Status"}*/}
      {/*      </Button>*/}
      {/*    </DialogActionsBar>*/}
      {/*  </Dialog>*/}
      {/*)}*/}

      {/*Add Enrollment Dialog*/}

      {showProviderDialog && (
        <Dialog
          title="File claim appeal"

          onClose={() => setShowProviderDialog(false)}

          /*width={500}*/
          width="min(500px, 95vw)"
        >
          <div
            style={{
              padding: "8px 0",
            }}
          >
            <div
              style={{
                fontSize: 13,

                color: "#6B7280",

                marginBottom: 12,
              }}
            >
              Select a Provider Admin to file claim appeal.
            </div>

            <label
              style={{
                display: "block",

                fontSize: 13,

                fontWeight: 600,

                color: "#374151",

                marginBottom: 7,
              }}
            >
              Provider Admin
            </label>

            <DropDownList
              data={providers}
              name="providerAdminDropDown"
              dataItemKey="id"

              textField="displayName"

              value={selectedProvider}

              filterable={true}

              filter={providerSearch}

              loading={providerLoading}

              onFilterChange={(event) => {
                const searchValue = event.filter?.value || "";

                handleProviderFilterChange(searchValue);
              }}

              onChange={(event) => {
                setSelectedProvider(event.value);
              }}

              placeholder="Search or select Provider Admin"

              itemRender={(li, itemProps) => {
                const provider = itemProps.dataItem;

                const fullName = [provider?.firstName, provider?.lastName]

                  .filter(Boolean)

                  .join(" ");

                return React.cloneElement(
                  li,

                  li.props,

                  <div
                    style={{
                      padding: "6px 2px",
                    }}
                  >
                    {/* Name */}

                    <div
                      style={{
                        fontSize: 14,

                        fontWeight: 600,

                        color: "#1F2937",

                        marginBottom: 4,
                      }}
                    >
                      {fullName || provider?.username || "Unknown Provider"}
                    </div>

                    {/* Email */}

                    {provider?.email && (
                      <div
                        style={{
                          fontSize: 12,

                          color: "#6B7280",

                          marginBottom: 2,
                        }}
                      >
                        {provider.email}
                      </div>
                    )}

                    {/* Username */}

                    {provider?.username && (
                      <div
                        style={{
                          fontSize: 12,

                          color: "#9CA3AF",
                        }}
                      >
                        Username: {provider.username}
                      </div>
                    )}
                  </div>,
                );
              }}

              valueRender={(element, value) => {
                if (!value) {
                  return element;
                }

                const provider = value;

                const fullName = [provider?.firstName, provider?.lastName]

                  .filter(Boolean)

                  .join(" ");

                return React.cloneElement(
                  element,

                  element.props,

                  <span>
                    {fullName || provider?.username || "Select Provider Admin"}
                  </span>,
                );
              }}

              style={{
                width: "100%",
              }}
            />

            <div
              style={{
                display: "flex",

                justifyContent: "flex-end",

                gap: 10,

                marginTop: 24,
              }}
            >
              <button
                type="button"

                onClick={() => setShowProviderDialog(false)}

                style={{
                  padding: "9px 16px",

                  borderRadius: 8,

                  border: "1px solid #D1D5DB",

                  background: "#fff",

                  color: "#374151",

                  fontSize: 13,

                  fontWeight: 600,

                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                type="button"

                disabled={!selectedProvider}

                onClick={handleProviderContinue}
                style={{
                  padding: "9px 16px",

                  borderRadius: 8,

                  border: "none",

                  background: selectedProvider ? "#0D9488" : "#CBD5E1",

                  color: "#fff",

                  fontSize: 13,

                  fontWeight: 700,

                  cursor: selectedProvider ? "pointer" : "not-allowed",
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/*================================================= */}

      {/*View Appeal Dialog*/}

      {/*================================================= */}

      {showDetailsDialog && selectedAppealDetails && (
        <Dialog
          title="Appeal Details"
          onClose={closeDetails}
                  
          width="min(700px, 95vw)"
          style={{
              zIndex: 1000              
          }}
        >
          <div
            style={{
              padding: "8px 0",
              maxHeight: "calc(90vh - 120px)",
              overflowY: "auto",
              
              overflowX: "hidden",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                paddingBottom: 18,
                marginBottom: 18,
                borderBottom: "1px solid #E8ECF0",
                flexWrap: "wrap",
              }}
            >
              <ProviderAvatar
                name={
                  selectedAppealDetails.providerName ||
                  selectedAppealDetails.memberName
                }
                size={52}
              />

              <div
                style={{
                  flex: "1 1 200px",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: "#1A1D21",
                    wordBreak: "break-word",
                  }}
                >
                  {selectedAppealDetails.appealNumber || "Appeal Details"}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 5,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "#6B7280",
                      wordBreak: "break-all",
                    }}
                  >
                    Claim #{selectedAppealDetails.claimNumber || "—"}
                  </span>

                  <StatusPill status={selectedAppealDetails.status} />
                </div>
              </div>
            </div>

            {/* Appeal Information */}
            <DetailSection title="Appeal Information" icon={FileText}>
              <DetailItem
                label="Appeal Number"
                value={selectedAppealDetails.appealNumber}
              />

              <DetailItem
                label="Claim Number"
                value={selectedAppealDetails.claimNumber}
              />

              {/*<DetailItem*/}
              {/*  label="Claim ID"*/}
              {/*  value={selectedAppealDetails.claimId}*/}
              {/*  mono*/}
              {/*/>*/}

              {/*<DetailItem*/}
              {/*  label="Group ID"*/}
              {/*  value={selectedAppealDetails.groupId}*/}
              {/*  mono*/}
              {/*/>*/}

              <DetailItem label="Status" value={selectedAppealDetails.status} />

              {/*<DetailItem*/}
              {/*    label="Created By"*/}
              {/*    value={selectedAppealDetails.createdBy}*/}
              {/*/>*/}

              <DetailItem
                label={
                  <div className="flex items-center gap-2">
                    <span>Submitted By</span>
                  </div>
                }
                value={(() => {
                  const [firstName, lastName, username, email] =
                    selectedAppealDetails.createdBy?.split("|") || [];

                  return (
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3 font-semibold text-gray-900">
                        <UserRound
                          size={14}
                          className="text-gray-400"
                          style={{ marginRight: "5px" }}
                        />
                        {firstName} {lastName}
                      </div>

                      {["super admin", "admin"].includes(
                        user?.userRole?.trim().toLowerCase(),
                      ) && (
                        <>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <AtSign size={13} style={{ marginRight: "5px" }} />
                            {username}
                          </div>

                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <Mail size={13} style={{ marginRight: "5px" }} />
                            {email}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              />

              <DetailItem
                label="Submitted"
                value={formatDateTime(selectedAppealDetails.createdDate)}
              />
            </DetailSection>

            {/* Member Information */}
            <DetailSection title="Member Information" icon={User}>
              <DetailItem
                label="Member Name"
                value={selectedAppealDetails.memberName}
              />

              <DetailItem
                label="Member ID"
                value={selectedAppealDetails.memberId}
                mono
              />
            </DetailSection>

            {/* Provider Information */}
            <DetailSection title="Provider Information" icon={Building2}>
              <DetailItem
                label="Provider Name"
                value={selectedAppealDetails.providerName}
              />

              <DetailItem label="NPI" value={selectedAppealDetails.npi} mono />

              <DetailItem label="TIN" value={selectedAppealDetails.tin} mono />
            </DetailSection>

            {/* Appeal Reason & Outcome */}
            <DetailSection title="Appeal Request" icon={MessageSquare}>
              <DetailItem label="Reason" value={selectedAppealDetails.reason} />

              <DetailItem
                label="Requested Outcome"
                value={selectedAppealDetails.requestedOutcome}
              />
            </DetailSection>

            {/* Contact Information */}
            <DetailSection title="Contact Information" icon={Phone}>
              <DetailItem
                label="Email"
                value={selectedAppealDetails.contactEmail}
              />

              <DetailItem
                label="Phone"
                value={selectedAppealDetails.contactPhone}
              />
            </DetailSection>

                      {/* Attachments */}

                      {/* Attachments */}
                      <DetailSection title="Attachments" icon={Paperclip}>
                          <div
                              style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 12,
                                  padding: 12,
                                  border: "1px solid #E8ECF0",
                                  borderRadius: 10,
                                  background: "#F8FAFB",
                                  width: "100%",
                                  minWidth: 0,
                                  boxSizing: "border-box",
                              }}
                          >
                              {/* Attachment information */}
                              <div
                                  style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                      minWidth: 0,
                                      width: "100%",
                                  }}
                              >
                                  <div
                                      style={{
                                          width: 34,
                                          height: 34,
                                          minWidth: 34,
                                          borderRadius: 8,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          background: "#E6FFFB",
                                          color: "#0D9488",
                                          flexShrink: 0,
                                      }}
                                  >
                                      <Paperclip size={16} strokeWidth={2.2} />
                                  </div>

                                  <div
                                      style={{
                                          flex: 1,
                                          minWidth: 0,
                                          overflowWrap: "anywhere",
                                      }}
                                  >
                                      <div
                                          style={{
                                              fontSize: 13,
                                              fontWeight: 700,
                                              color: "#1A1D21",
                                              lineHeight: 1.4,
                                          }}
                                      >
                                          {selectedAppealDetails.attachmentCount ?? 0} Attachment
                                          {(selectedAppealDetails.attachmentCount ?? 0) !== 1
                                              ? "s"
                                              : ""}
                                      </div>

                                      <div
                                          style={{
                                              fontSize: 11,
                                              color: "#6B7280",
                                              marginTop: 3,
                                              lineHeight: 1.5,
                                          }}
                                      >
                                          Supporting documents for this appeal
                                      </div>
                                  </div>
                              </div>

                              {/* View attachments button */}
                              <button
                                  type="button"
                                  onClick={() => handleOpenAttachments(selectedAppealDetails)}
                                  data-cy="view-attachments-button-list-page"
                                  style={{
                                      padding: "10px 12px",
                                      width: "100%",
                                      minHeight: 40,
                                      boxSizing: "border-box",
                                      borderRadius: 8,
                                      border: "1px solid #0D9488",
                                      background: "#fff",
                                      color: "#0D9488",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: 6,
                                      fontFamily: "inherit",
                                      whiteSpace: "normal",
                                      touchAction: "manipulation",
                                  }}
                              >
                                  <Paperclip size={14} strokeWidth={2.4} />
                                  View Attachments
                              </button>
                          </div>
                      </DetailSection>

            {/*<DetailSection title="Attachments" icon={Paperclip}>*/}
            {/*  <div*/}
            {/*    style={{*/}
            {/*      display: "flex",*/}
            {/*      alignItems: "center",*/}
            {/*      justifyContent: "space-between",*/}
            {/*      gap: 12,*/}
            {/*      padding: "12px 14px",*/}
            {/*      border: "1px solid #E8ECF0",*/}
            {/*      borderRadius: 10,*/}
            {/*      background: "#F8FAFB",*/}
            {/*      flexWrap: "wrap",*/}
            {/*    }}*/}
            {/*  >*/}
            {/*    <div*/}
            {/*      style={{*/}
            {/*        display: "flex",*/}
            {/*        alignItems: "center",*/}
            {/*        gap: 10,*/}
            {/*        minWidth: 0,*/}
            {/*      }}*/}
            {/*    >*/}
            {/*      <div*/}
            {/*        style={{*/}
            {/*          width: 34,*/}
            {/*          height: 34,*/}
            {/*          borderRadius: 8,*/}
            {/*          display: "flex",*/}
            {/*          alignItems: "center",*/}
            {/*          justifyContent: "center",*/}
            {/*          background: "#E6FFFB",*/}
            {/*          color: "#0D9488",*/}
            {/*          flexShrink: 0,*/}
            {/*        }}*/}
            {/*      >*/}
            {/*        <Paperclip size={16} strokeWidth={2.2} />*/}
            {/*      </div>*/}

            {/*      <div>*/}
            {/*        <div*/}
            {/*          style={{*/}
            {/*            fontSize: 13,*/}
            {/*            fontWeight: 700,*/}
            {/*            color: "#1A1D21",*/}
            {/*          }}*/}
            {/*        >*/}
            {/*          {selectedAppealDetails.attachmentCount ?? 0} Attachment*/}
            {/*          {(selectedAppealDetails.attachmentCount ?? 0) !== 1*/}
            {/*            ? "s"*/}
            {/*            : ""}*/}
            {/*        </div>*/}

            {/*        <div*/}
            {/*          style={{*/}
            {/*            fontSize: 11,*/}
            {/*            color: "#6B7280",*/}
            {/*            marginTop: 2,*/}
            {/*          }}*/}
            {/*        >*/}
            {/*          Supporting documents for this appeal*/}
            {/*        </div>*/}
            {/*      </div>*/}
            {/*    </div>*/}

            {/*    <button*/}
            {/*      type="button"*/}
            {/*      onClick={() => handleOpenAttachments(selectedAppealDetails)}*/}
            {/*      data-cy="view-attachments-button-list-page"*/}
            {/*      style={{*/}
            {/*        padding: "7px 12px",*/}
            {/*        borderRadius: 8,*/}
            {/*        border: "1px solid #0D9488",*/}
            {/*        background: "#fff",*/}
            {/*        color: "#0D9488",*/}
            {/*        fontSize: 12,*/}
            {/*        fontWeight: 700,*/}
            {/*        cursor: "pointer",*/}
            {/*        display: "inline-flex",*/}
            {/*        alignItems: "center",*/}
            {/*        justifyContent: "center",*/}
            {/*        gap: 6,*/}
            {/*        fontFamily: "inherit",*/}
            {/*        whiteSpace: "nowrap",*/}
            {/*      }}*/}
            {/*    >*/}
            {/*      <Paperclip size={13} strokeWidth={2.4} />*/}
            {/*      View Attachments*/}
            {/*    </button>*/}
            {/*  </div>*/}
            {/*</DetailSection>*/}

            {/* Admin Note */}
            {selectedAppealDetails.adminNote && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: "#F8FAFB",
                  borderRadius: 10,
                  overflowWrap: "anywhere",
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6B7280",
                    marginBottom: 5,
                  }}
                >
                  Admin Note
                </div>

                <div
                  style={{
                    fontSize: 13,
                    color: "#1A1D21",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {selectedAppealDetails.adminNote}
                </div>
              </div>
            )}
          </div>

          <DialogActionsBar>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={closeDetails}
                data-cy="close-appeal-details-button"
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "1px solid #E8ECF0",
                  background: "#fff",
                  color: "#6B7280",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Close
              </button>

              <button
                type="button"
                onClick={() => {
                  handleAppealAction(
                    selectedAppealDetails.appealId,
                    selectedAppealDetails.claimNumber,
                    selectedAppealDetails.userId,
                  );
                }}
                data-cy="edit-appeal-button"
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: "linear-gradient(135deg, #0D9488, #0F766E)",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <Edit2
                  size={13}
                  style={{
                    marginRight: 5,
                  }}
                />
                Edit
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}

      {/* ==================================================

                STATUS HISTORY DIALOG

            ================================================== */}

      {historyDialog && (
        <Dialog
          title="Claim Appeal Status History"
          onClose={closeHistoryDialog}
          width={viewportWidth < 640 ? Math.max(viewportWidth - 24, 300) : 680}
        >
          <div
            style={{
              padding: "4px 0",
              maxHeight: 520,
              overflowY: "auto",
            }}
          >
            {/* Appeal Header */}
            <div
              style={{
                padding: 14,
                borderRadius: 12,
                background: "#F8FAFC",
                border: "1px solid #E8ECF0",
                marginBottom: 20,
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#1A1D21",
                }}
              >
                {historyDialog.appealNumber || "Claim Appeal"}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 14,
                  flexWrap: "wrap",
                  marginTop: 7,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                  }}
                >
                  Appeal #{historyDialog.appealNumber || "—"}
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                  }}
                >
                  Claim: <strong>{historyDialog.claimNumber || "—"}</strong>
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                  }}
                >
                  Member: <strong>{historyDialog.memberName || "—"}</strong>
                </span>

                <span
                  style={{
                    fontSize: 11,
                    color: "#6B7280",
                  }}
                >
                  Provider: <strong>{historyDialog.providerName || "—"}</strong>
                </span>
              </div>
            </div>

            {historyLoading && (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                }}
              >
                <LoadingSpinner message="Loading appeal status history..." />
              </div>
            )}

            {!historyLoading && history.length === 0 && (
              <div
                style={{
                  padding: 40,
                  textAlign: "center",
                  color: "#9CA3AF",
                  fontSize: 13,
                }}
              >
                No appeal status history found.
              </div>
            )}

            {!historyLoading && history.length > 0 && (
              <div
                style={{
                  position: "relative",
                  paddingLeft: 30,
                }}
              >
                {/* Timeline line */}
                <div
                  style={{
                    position: "absolute",
                    left: 9,
                    top: 8,
                    bottom: 8,
                    width: 2,
                    background: "#E8ECF0",
                  }}
                />

                {history.map((item, index) => {
                  const status = item.Status ?? item.status ?? "";

                  const changedBy = item.ChangedBy ?? item.changedBy ?? "";

                  const changedDate =
                    item.ChangedDate ?? item.changedDate ?? "";

                  const notes = item.Notes ?? item.notes ?? "";

                  const meta = STATUS_META[status] || {
                    bg: "#F3F4F6",
                    color: "#6B7280",
                    icon: CircleAlert,
                  };

                  const Icon = meta.icon;

                  return (
                    <div
                      key={
                        item.ClaimAppealStatusAuditId ??
                        item.appealStatusAuditId ??
                        item.id ??
                        index
                      }
                      style={{
                        position: "relative",
                        paddingBottom: index === history.length - 1 ? 0 : 22,
                      }}
                    >
                      {/* Timeline icon */}
                      <div
                        style={{
                          position: "absolute",
                          left: -30,
                          top: 0,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: meta.bg,
                          color: meta.color,
                          border: "2px solid #fff",
                          boxShadow: "0 0 0 1px #E8ECF0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1,
                        }}
                      >
                        <Icon size={10} strokeWidth={2.5} />
                      </div>

                      {/* History item */}
                      <div
                        style={{
                          padding: "11px 13px",
                          border: "1px solid #E8ECF0",
                          borderRadius: 11,
                          background: "#fff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                          <StatusBadge status={status} />

                          <span
                            style={{
                              fontSize: 10.5,
                              color: "#9CA3AF",
                            }}
                          >
                            {formatDateTime(changedDate)}
                          </span>
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 12,
                            color: "#475569",
                          }}
                        >
                          <strong>Changed by:</strong> {changedBy || "System"}
                        </div>

                        {notes && (
                          <div
                            style={{
                              marginTop: 7,
                              padding: "8px 10px",
                              background: "#F8FAFC",
                              borderRadius: 8,
                              fontSize: 12,
                              color: "#64748B",
                              lineHeight: 1.45,
                            }}
                          >
                            {notes}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogActionsBar>
            <Button
              data-cy="close-history-dialog-button"
              onClick={closeHistoryDialog}
            >
              Close
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}

      {/*{historyDialog && (*/}
      {/*  <Dialog*/}
      {/*    title="ERA Enrollment Status History"*/}

      {/*    onClose={closeHistoryDialog}*/}

      {/*    width={*/}
      {/*      viewportWidth < 640*/}
      {/*        ? Math.max(*/}
      {/*            viewportWidth - 24,*/}

      {/*            300,*/}
      {/*          )*/}
      {/*        : 680*/}
      {/*    }*/}
      {/*  >*/}
      {/*    <div*/}
      {/*      style={{*/}
      {/*        padding: "4px 0",*/}

      {/*        maxHeight: 520,*/}

      {/*        overflowY: "auto",*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      */}
      {/* Enrollment Header */}

      {/*      <div*/}
      {/*        style={{*/}
      {/*          padding: 14,*/}

      {/*          borderRadius: 12,*/}

      {/*          background: "#F8FAFC",*/}

      {/*          border: "1px solid #E8ECF0",*/}

      {/*          marginBottom: 20,*/}
      {/*        }}*/}
      {/*      >*/}
      {/*        <div*/}
      {/*          style={{*/}
      {/*            fontSize: 15,*/}

      {/*            fontWeight: 700,*/}

      {/*            color: "#1A1D21",*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          {getEnrollmentName(historyDialog)}*/}
      {/*        </div>*/}

      {/*        <div*/}
      {/*          style={{*/}
      {/*            display: "flex",*/}

      {/*            gap: 14,*/}

      {/*            flexWrap: "wrap",*/}

      {/*            marginTop: 7,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          <span*/}
      {/*            style={{*/}
      {/*              fontSize: 11,*/}

      {/*              color: "#6B7280",*/}
      {/*            }}*/}
      {/*          >*/}
      {/*            Enrollment #{historyDialog.id}*/}
      {/*          </span>*/}

      {/*          <span*/}
      {/*            style={{*/}
      {/*              fontSize: 11,*/}

      {/*              color: "#6B7280",*/}
      {/*            }}*/}
      {/*          >*/}
      {/*            TIN: <strong>{historyDialog.tin || "—"}</strong>*/}
      {/*          </span>*/}

      {/*          <span*/}
      {/*            style={{*/}
      {/*              fontSize: 11,*/}

      {/*              color: "#6B7280",*/}
      {/*            }}*/}
      {/*          >*/}
      {/*            NPI: <strong>{historyDialog.npi || "—"}</strong>*/}
      {/*          </span>*/}
      {/*        </div>*/}
      {/*      </div>*/}

      {/*      {historyLoading && (*/}
      {/*        <div*/}
      {/*          style={{*/}
      {/*            padding: 40,*/}

      {/*            textAlign: "center",*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          <LoadingSpinner message="Loading status history..." />*/}
      {/*        </div>*/}
      {/*      )}*/}

      {/*      {!historyLoading && history.length === 0 && (*/}
      {/*        <div*/}
      {/*          style={{*/}
      {/*            padding: 40,*/}

      {/*            textAlign: "center",*/}

      {/*            color: "#9CA3AF",*/}

      {/*            fontSize: 13,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          No status history found.*/}
      {/*        </div>*/}
      {/*      )}*/}

      {/*      {!historyLoading && history.length > 0 && (*/}
      {/*        <div*/}
      {/*          style={{*/}
      {/*            position: "relative",*/}

      {/*            paddingLeft: 30,*/}
      {/*          }}*/}
      {/*        >*/}
      {/*          */}
      {/* Timeline line */}

      {/*          <div*/}
      {/*            style={{*/}
      {/*              position: "absolute",*/}

      {/*              left: 9,*/}

      {/*              top: 8,*/}

      {/*              bottom: 8,*/}

      {/*              width: 2,*/}

      {/*              background: "#E8ECF0",*/}
      {/*            }}*/}
      {/*          />*/}

      {/*          {history.map((item, index) => {*/}
      {/*            const status = item.Status ?? item.status ?? "";*/}

      {/*            const changedBy = item.ChangedBy ?? item.changedBy ?? "";*/}

      {/*            const changedDate =*/}
      {/*              item.ChangedDate ?? item.changedDate ?? "";*/}

      {/*            const notes = item.Notes ?? item.notes ?? "";*/}

      {/*            const meta = STATUS_META[status] || {*/}
      {/*              bg: "#F3F4F6",*/}

      {/*              color: "#6B7280",*/}

      {/*              icon: CircleAlert,*/}
      {/*            };*/}

      {/*            const Icon = meta.icon;*/}

      {/*            return (*/}
      {/*              <div*/}
      {/*                key={*/}
      {/*                  item.ERAEnrollmentStatusAuditId ??*/}
      {/*                  item.eFTEnrollmentStatusAuditId ??*/}
      {/*                  index*/}
      {/*                }*/}

      {/*                style={{*/}
      {/*                  position: "relative",*/}

      {/*                  paddingBottom: index === history.length - 1 ? 0 : 22,*/}
      {/*                }}*/}
      {/*              >*/}
      {/*                */}
      {/* Timeline icon */}

      {/*                <div*/}
      {/*                  style={{*/}
      {/*                    position: "absolute",*/}

      {/*                    left: -30,*/}

      {/*                    top: 0,*/}

      {/*                    width: 20,*/}

      {/*                    height: 20,*/}

      {/*                    borderRadius: "50%",*/}

      {/*                    background: meta.bg,*/}

      {/*                    color: meta.color,*/}

      {/*                    border: "2px solid #fff",*/}

      {/*                    boxShadow: "0 0 0 1px #E8ECF0",*/}

      {/*                    display: "flex",*/}

      {/*                    alignItems: "center",*/}

      {/*                    justifyContent: "center",*/}

      {/*                    zIndex: 1,*/}
      {/*                  }}*/}
      {/*                >*/}
      {/*                  <Icon*/}
      {/*                    size={10}*/}

      {/*                    strokeWidth={2.5}*/}
      {/*                  />*/}
      {/*                </div>*/}

      {/*                <div*/}
      {/*                  style={{*/}
      {/*                    padding: "11px 13px",*/}

      {/*                    border: "1px solid #E8ECF0",*/}

      {/*                    borderRadius: 11,*/}

      {/*                    background: "#fff",*/}
      {/*                  }}*/}
      {/*                >*/}
      {/*                  <div*/}
      {/*                    style={{*/}
      {/*                      display: "flex",*/}

      {/*                      alignItems: "center",*/}

      {/*                      justifyContent: "space-between",*/}

      {/*                      gap: 10,*/}

      {/*                      flexWrap: "wrap",*/}
      {/*                    }}*/}
      {/*                  >*/}
      {/*                    <StatusBadge status={status} />*/}

      {/*                    <span*/}
      {/*                      style={{*/}
      {/*                        fontSize: 10.5,*/}

      {/*                        color: "#9CA3AF",*/}
      {/*                      }}*/}
      {/*                    >*/}
      {/*                      {formatDateTime(changedDate)}*/}
      {/*                    </span>*/}
      {/*                  </div>*/}

      {/*                  <div*/}
      {/*                    style={{*/}
      {/*                      marginTop: 8,*/}

      {/*                      fontSize: 12,*/}

      {/*                      color: "#475569",*/}
      {/*                    }}*/}
      {/*                  >*/}
      {/*                    <strong>Changed by:</strong> {changedBy || "System"}*/}
      {/*                  </div>*/}

      {/*                  {notes && (*/}
      {/*                    <div*/}
      {/*                      style={{*/}
      {/*                        marginTop: 7,*/}

      {/*                        padding: "8px 10px",*/}

      {/*                        background: "#F8FAFC",*/}

      {/*                        borderRadius: 8,*/}

      {/*                        fontSize: 12,*/}

      {/*                        color: "#64748B",*/}

      {/*                        lineHeight: 1.45,*/}
      {/*                      }}*/}
      {/*                    >*/}
      {/*                      {notes}*/}
      {/*                    </div>*/}
      {/*                  )}*/}
      {/*                </div>*/}
      {/*              </div>*/}
      {/*            );*/}
      {/*          })}*/}
      {/*        </div>*/}
      {/*      )}*/}
      {/*    </div>*/}

      {/*    <DialogActionsBar>*/}
      {/*      <Button onClick={closeHistoryDialog}>Close</Button>*/}
      {/*    </DialogActionsBar>*/}
      {/*  </Dialog>*/}
      {/*    )}*/}

      <AppealAttachmentsDialog
        open={attachmentDialogOpen}
        appeal={selectedAppeal}
        onClose={handleCloseAttachments}
        onPreview={handlePreviewAttachment}
      />

      <AttachmentPreviewDialog
        open={previewDialogOpen}
        attachment={previewAttachment}
        onClose={handleClosePreview}
      />
    </div>
  );
};

const DetailSection = ({
  title,

  icon: Icon,

  children,
}) => (
  <div
    style={{
      marginBottom: 20,
    }}
  >
    <div
      style={{
        display: "flex",

        alignItems: "center",

        gap: 8,

        paddingBottom: 9,

        marginBottom: 12,

        borderBottom: "1px solid #E8ECF0",
      }}
    >
      <Icon
        size={16}

        color="#0D9488"
      />

      <span
        style={{
          fontSize: 14,

          fontWeight: 700,

          color: "#1A1D21",
        }}
      >
        {title}
      </span>
    </div>

    <div
      style={{
        display: "grid",

        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",

        gap: 14,
      }}
    >
      {children}
    </div>
  </div>
);

const DetailItem = ({
  label,

  value,

  mono = false,
}) => (
  <div>
    <div
      style={{
        fontSize: 11,

        color: "#9CA3AF",

        marginBottom: 3,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontSize: 13,

        color: "#1A1D21",

        fontWeight: 600,

        fontFamily: mono ? "monospace" : "inherit",

        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </div>
  </div>
);

/* ============================================================

   EMPTY STATE

============================================================ */

const EmptyState = ({ hasFilters }) => (
  <div
    className="card"

    style={{
      textAlign: "center",

      padding: "56px 24px",
    }}
  >
    <div
      style={{
        width: 64,

        height: 64,

        borderRadius: 16,

        background: "rgba(13,148,136,.08)",

        display: "inline-flex",

        alignItems: "center",

        justifyContent: "center",

        marginBottom: 16,
      }}
    >
      {hasFilters ? (
        <Search
          size={28}

          color="#0D9488"

          strokeWidth={2}
        />
      ) : (
        <Landmark
          size={28}

          color="#0D9488"

          strokeWidth={2}
        />
      )}
    </div>

    <div
      style={{
        fontSize: 17,

        fontWeight: 700,

        color: "#1A1D21",

        marginBottom: 6,
      }}
    >
      {hasFilters
        ? "No Claim Appeals match your filters"
        : "No Claim Appeals yet"}
    </div>

    <div
      style={{
        fontSize: 13,

        color: "#6B7280",
      }}
    >
      {hasFilters
        ? "Try clearing the filters or adjusting the search."
        : "Claim Appeals will appear here when providers submit them."}
    </div>
  </div>
);

/* ============================================================

   STYLES

============================================================ */

const styles = {
  overlay: {
    position: "fixed",

    inset: 0,

    backgroundColor: "rgba(255,255,255,.6)",

    display: "flex",

    justifyContent: "center",

    alignItems: "center",

    zIndex: 9999,
  },
};

const iconBtn = (color) => ({
  width: 30,

  height: 30,

  borderRadius: 8,

  border: `1px solid ${color}33`,

  background: `${color}0A`,

  color,

  cursor: "pointer",

  display: "inline-flex",

  alignItems: "center",

  justifyContent: "center",

  padding: 0,
});

const smallActionButton = (color) => ({
  padding: "6px 10px",

  borderRadius: 8,

  border: `1px solid ${color}33`,

  background: `${color}0A`,

  color,

  fontSize: 11,

  fontWeight: 700,

  cursor: "pointer",

  display: "inline-flex",

  alignItems: "center",

  gap: 5,

  fontFamily: "inherit",
});

const selectStyle = {
  padding: "8px 12px",

  borderRadius: 10,

  border: "1.5px solid #E8ECF0",

  fontSize: 13,

  fontWeight: 600,

  color: "#374151",

  background: "#F8FAFB",

  cursor: "pointer",

  fontFamily: "inherit",

  outline: "none",
};

const toggleBtnStyle = (active) => ({
  padding: "7px 12px",

  borderRadius: 8,

  border: "none",

  background: active ? "#fff" : "transparent",

  color: active ? "#0D9488" : "#6B7280",

  fontSize: 12,

  fontWeight: 700,

  cursor: "pointer",

  display: "inline-flex",

  alignItems: "center",

  gap: 5,

  boxShadow: active ? "0 1px 3px rgba(0,0,0,.08)" : "none",

  fontFamily: "inherit",

  transition: "all .15s",
});

export default ClaimAppealListPage;
